import Ajv from "ajv";
import { TaskType } from "@coworker/apprestructured/src/task-management/types/TaskType";
import {
  isLoading,
  LOADING,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";

const ajv = new Ajv();
const schemaValidationCache = new Map<string, ReturnType<typeof ajv.compile>>();

/**
 * Validates initial field values from query parameters against the JSON Schema
 * defined in the task type configuration.
 *
 * This ensures that data from URL params conforms to the expected structure
 * before populating form fields.
 *
 * @param value - The initial value to validate (typically from search params).
 * @param fieldPath - Dot-notation path to the field (e.g., "details.item").
 * @param taskType - The task type containing the schema definitions in its details config.
 * @returns The validated value cast to type T, `null` if validation fails, or `LOADING` if data isn't ready.
 *
 * @example
 * const itemValue = validateInitialFieldValue<ItemNoAndType>(
 *   searchParams.details?.item,
 *   "details.item",
 *   taskType
 * );
 */
export function validateInitialDetailsFieldValue<T>(
  value: unknown | Loading,
  fieldPath: string,
  taskType: TaskType | Loading
): T | null | Loading {
  if (isLoading(value) || isLoading(taskType)) {
    return LOADING;
  }

  // Extract the detail name from the field path (e.g., "details.item" -> "item")
  const detailName = fieldPath.split(".").pop() ?? fieldPath;

  // Find the corresponding schema from task type configuration
  const schemaConfig = taskType.details.find(
    (detail) => detail.name === detailName
  )?.schema;

  // If no schema exists or is empty, consider the field invalid
  if (!schemaConfig || Object.keys(schemaConfig).length === 0) {
    return null;
  }

  const cacheKey = `${taskType.id}-${detailName}`;
  let validate = schemaValidationCache.get(cacheKey);
  if (!validate) {
    validate = ajv.compile(schemaConfig);
    schemaValidationCache.set(cacheKey, validate);
  }
  if (!validate(value)) {
    return null;
  }
  return value as T;
}

/**
 * Type guard that validates a value is a non-empty string.
 *
 * @param value - The value to check.
 * @returns The value as a string if valid, otherwise `null`.
 */
export const validateStringType = (
  value: unknown | Loading
): string | Loading | null => {
  if (isLoading(value)) {
    return LOADING;
  }
  if (typeof value !== "string") {
    return null;
  }
  return value;
};

export const validateBooleanType = (
  value: unknown | Loading
): boolean | null | Loading => {
  if (isLoading(value)) {
    return LOADING;
  }
  if (typeof value !== "boolean") {
    return null;
  }
  return value;
};

export const validateArrayType = <T>(
  value: unknown | Loading,
  _fieldPath?: string,
  _taskType?: TaskType | Loading
): T[] | Loading => {
  if (isLoading(value)) {
    return LOADING;
  }
  if (!Array.isArray(value)) {
    return [];
  }
  return value as T[];
};

export const getTypedValue = <T>(value: unknown | Loading): T | Loading => {
  if (isLoading(value)) {
    return LOADING;
  }
  return value as T;
};
